import type { Gender } from "../game/Types";
import { createSignalingTransport, type ConnectionMode } from "./createSignalingTransport";
import type { PlayerIdentity, PlayerLocation, SignalingTransport } from "./SignalingTransport";

export interface LobbyPeer {
  peerId: string;
  name: string;
  gender: Gender;
  order: number;
}

export interface LobbyInfo {
  code: string;
  name: string;
  mode: "deathmatch" | "teams";
  mapId: string;
  maxPlayers: number;
  hostPeerId: string;
  started: boolean;
  distanceKm?: number;
  peers: LobbyPeer[];
}

export interface JoinRequestInfo {
  requestId: string;
  peerId: string;
  name: string;
  gender: Gender;
}

export interface SignalMessage {
  fromId: string;
  description?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
}

interface PendingJoin {
  requestId: string;
  timer: number;
  resolve: (lobby: LobbyInfo) => void;
  reject: (error: Error) => void;
}

const JOIN_REQUEST_TIMEOUT_MS = 120_000;

export class SignalingClient extends EventTarget {
  private transport: SignalingTransport | null = null;
  private readonly signalBacklog: SignalMessage[] = [];
  private signalConsumer: ((message: SignalMessage) => void) | null = null;
  private pendingJoin: PendingJoin | null = null;
  private playerLocation: PlayerLocation | undefined;
  private analyticsId: string | undefined;

  constructor(
    readonly peerId: string,
    private playerName: string,
    private playerGender: Gender,
    readonly mode: ConnectionMode = "online",
  ) {
    super();
  }

  async connect(): Promise<void> {
    if (this.transport?.connected) return;
    if (!this.transport) {
      this.transport = createSignalingTransport(this.mode);
      this.bindEvents(this.transport);
    }
    await this.transport.connect(this.identity());
  }

  get iceSessionToken(): string | null {
    return this.transport?.sessionToken ?? null;
  }

  setPlayerIdentity(name: string, gender: Gender): void {
    this.playerName = name;
    this.playerGender = gender;
    this.transport?.setIdentity(this.identity());
  }

  setPlayerLocation(location: PlayerLocation | undefined): void {
    this.playerLocation = location;
    this.transport?.setIdentity(this.identity());
  }

  setAnalyticsId(analyticsId: string | undefined): void {
    this.analyticsId = analyticsId;
    this.transport?.setIdentity(this.identity());
  }

  listLobbies(): Promise<LobbyInfo[]> {
    return this.request("lobby:list", { location: this.playerLocation });
  }

  createLobby(request: { name: string; mode: "deathmatch" | "teams"; mapId: string; maxPlayers: number }): Promise<LobbyInfo> {
    return this.request("lobby:create", request);
  }

  requestJoin(code: string): Promise<LobbyInfo> {
    if (this.pendingJoin) return Promise.reject(new Error("A join request is already waiting for approval."));
    return new Promise((resolve, reject) => {
      void this.request<{ requestId: string }>("lobby:join", { code }).then((reply) => {
        const timer = window.setTimeout(() => this.rejectPendingJoin("The host did not answer the join request."), JOIN_REQUEST_TIMEOUT_MS);
        this.pendingJoin = { requestId: reply.requestId, timer, resolve, reject };
      }, reject);
    });
  }

  respondToJoin(requestId: string, accepted: boolean): Promise<void> {
    return this.request("lobby:join-response", { requestId, accepted });
  }

  startLobby(): Promise<void> {
    return this.request("lobby:start", {});
  }

  sendSignal(targetId: string, payload: { description?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit }): void {
    this.transport?.notify("signal", { targetId, ...payload });
  }

  setSignalConsumer(consumer: (message: SignalMessage) => void): void {
    this.signalConsumer = consumer;
    for (const message of this.signalBacklog.splice(0)) consumer(message);
  }

  disconnect(): void {
    this.rejectPendingJoin("Join request cancelled.");
    this.transport?.disconnect();
    this.transport = null;
  }

  private request<T>(event: string, data: object): Promise<T> {
    if (!this.transport) return Promise.reject(new Error("The signaling connection is not ready."));
    return this.transport.request<T>(event, data);
  }

  private bindEvents(transport: SignalingTransport): void {
    transport.addEventListener("signal", (event) => {
      const message = (event as CustomEvent<SignalMessage>).detail;
      if (this.signalConsumer) this.signalConsumer(message);
      else this.signalBacklog.push(message);
    });
    for (const [source, target] of FORWARDED_EVENTS) {
      transport.addEventListener(source, (event) => this.emit(target, (event as CustomEvent).detail));
    }
    transport.addEventListener("lobby:join-approved", (event) => {
      const detail = (event as CustomEvent<{ requestId: string; lobby: LobbyInfo }>).detail;
      if (!this.pendingJoin || this.pendingJoin.requestId !== detail.requestId) return;
      this.takePendingJoin()?.resolve(detail.lobby);
    });
    transport.addEventListener("lobby:join-rejected", (event) => {
      const detail = (event as CustomEvent<{ requestId: string; reason: string }>).detail;
      if (this.pendingJoin?.requestId === detail.requestId) this.rejectPendingJoin(detail.reason);
    });
    transport.addEventListener("offline", () => {
      this.rejectPendingJoin("The signaling connection closed.");
      this.emit("offline", null);
    });
  }

  private identity(): PlayerIdentity {
    return {
      peerId: this.peerId,
      name: this.playerName,
      gender: this.playerGender,
      ...(this.playerLocation ? { location: this.playerLocation } : {}),
      ...(this.analyticsId ? { analyticsId: this.analyticsId } : {}),
    };
  }

  private rejectPendingJoin(reason: string): void {
    this.takePendingJoin()?.reject(new Error(reason));
  }

  private takePendingJoin(): PendingJoin | null {
    const pending = this.pendingJoin;
    if (pending) window.clearTimeout(pending.timer);
    this.pendingJoin = null;
    return pending;
  }

  private emit(name: string, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(name, { detail }));
  }
}

const FORWARDED_EVENTS = [
  ["lobby:updated", "lobby-updated"],
  ["lobby:list-updated", "lobby-list-updated"],
  ["lobby:peer-joined", "peer-joined"],
  ["lobby:peer-left", "peer-left"],
  ["lobby:join-request", "join-request"],
  ["lobby:join-request-cancelled", "join-request-cancelled"],
  ["lobby:closed", "lobby-closed"],
] as const;
